// AGENT MODE — the content views (WS-AGENT-MODE-1): board, list, detail,
// dashboard, chat and Ask AI, as real DOM. Each copies its TUI view's layout
// and words (smbls cli/tui views/*.js); none decides anything — selection,
// filtering, paging and what a key does are the shared runtime's. Every
// factory reads the snapshot through functions/agentModeView.js.
//
// The view that shows is the snapshot's content type (`hide`, Rule 18).
// Clicks hand the runtime the payload the TUI's hit zones carry
// (functions/agentModeRuntime.js agentModeClick); a second click of a pair is
// the TUI's double click — open.

export const AgentModeContent = {
  tag: 'main',
  data: {
    agentRegion: 'content',
    agentView: (el, s) => el.call('agentModeContentOf', el.call('agentModeSnapshot', s.root)).type
  },
  display: 'flex',
  flexDirection: 'column',
  flex: '1',
  minWidth: '0',
  minHeight: '0',
  overflow: 'auto',
  padding: 'Z A',
  // The scroller and the model's rows agree (functions/agentModeScroll.js):
  // sizes for the page keys after every render, the model's row → scrollTop,
  // a native scroll → the model's row.
  onRender: (el) => {
    el.call('agentModeReportScroll', el)
    el.call('agentModeSyncScroll', el)
  },
  stateDeps: [(el, s) => Number(s.root._agentModeRev) || 0],
  onStateUpdate: (el) => {
    el.call('agentModeReportScroll', el)
    el.call('agentModeSyncScroll', el)
  },
  onScroll: (e, el) => el.call('agentModeReportScroll', el, { top: true }),

  AgentModeLoading: {
    role: 'status',
    color: 'caption',
    hide: (el, s) =>
      el.call('agentModeContentState', el.call('agentModeSnapshot', s.root)) !== 'loading',
    text: (el, s) => {
      const c = el.call('agentModeContentOf', el.call('agentModeSnapshot', s.root))
      return el
        .call('polyglot', 'workspace.agentMode.loading')
        .replace('{view}', c.label.toLowerCase())
    }
  },

  AgentModeError: {
    role: 'alert',
    flow: 'y',
    gap: 'Y',
    hide: (el, s) =>
      el.call('agentModeContentState', el.call('agentModeSnapshot', s.root)) !== 'error',
    AgentModeErrorText: {
      color: 'dangerInk',
      text: (el, s) => {
        const c = el.call('agentModeContentOf', el.call('agentModeSnapshot', s.root))
        return (c.error && c.error.message) || ''
      }
    },
    AgentModeErrorHint: {
      color: 'caption',
      text: (el, s) => {
        const c = el.call('agentModeContentOf', el.call('agentModeSnapshot', s.root))
        return (c.error && c.error.hint) || ''
      }
    },
    AgentModeErrorKeys: {
      marginTop: 'Z',
      color: 'caption',
      text: (el) => el.call('polyglot', 'workspace.agentMode.retryKeys')
    }
  },

  AgentModeBoardView: {
    hide: (el, s) => !el.call('agentModeViewShown', s.root, 'board'),
    flex: '1',
    minHeight: '0',
    display: 'flex',
    AgentModeBoard: {}
  },
  AgentModeListView: {
    hide: (el, s) => !el.call('agentModeViewShown', s.root, 'list'),
    AgentModeList: {}
  },
  AgentModeDetailView: {
    hide: (el, s) => !el.call('agentModeViewShown', s.root, 'detail'),
    AgentModeDetail: {}
  },
  AgentModeDashboardView: {
    hide: (el, s) => !el.call('agentModeViewShown', s.root, 'dashboard'),
    flex: '1',
    minHeight: '0',
    display: 'flex',
    AgentModeDashboard: {}
  },
  AgentModeChatView: {
    hide: (el, s) => !el.call('agentModeViewShown', s.root, 'chat'),
    flex: '1',
    minHeight: '0',
    display: 'flex',
    AgentModeChat: {}
  },
  AgentModeAssistantView: {
    hide: (el, s) => !el.call('agentModeViewShown', s.root, 'assistant'),
    AgentModeAssistant: {}
  }
}

// ── board ──────────────────────────────────────────────────────────────────

// Status columns of cards (views/board.js). The dashboard reuses the columns.
export const AgentModeBoard = {
  role: 'group',
  data: { agentBoard: true },
  display: 'flex',
  gap: 'B',
  flex: '1',
  minWidth: '0',
  minHeight: '0',
  overflowX: 'auto',
  childExtends: 'AgentModeBoardColumn',
  childrenAs: 'state',
  children: (el, s) => {
    const c = el.call('agentModeContentOf', el.call('agentModeSnapshot', s.root))
    return c.type === 'board' ? el.call('agentModeBoardModel', s.root) : []
  }
}

export const AgentModeBoardEmpty = {
  color: 'caption',
  hide: (el, s) => {
    const c = el.call('agentModeContentOf', el.call('agentModeSnapshot', s.root))
    return c.type !== 'board' || el.call('agentModeBoardModel', s.root).length > 0
  },
  text: (el, s) => {
    const c = el.call('agentModeContentOf', el.call('agentModeSnapshot', s.root))
    return c.empty || el.call('polyglot', 'workspace.agentMode.nothingHere')
  }
}

export const AgentModeBoardColumn = {
  tag: 'section',
  data: { agentColumn: (el, s) => s.column },
  display: 'flex',
  flexDirection: 'column',
  flex: '1 1 0',
  minWidth: 'F',
  minHeight: '0',
  aria: { label: (el, s) => s.label },
  AgentModeColumnHead: {
    display: 'flex',
    gap: 'Y',
    paddingBottom: 'Y',
    borderBottomWidth: 'hairline',
    borderBottomStyle: 'solid',
    borderBottomColor: 'line',
    AgentModeColumnLabel: {
      tag: 'h2',
      margin: '0',
      fontSize: 'inherit',
      fontWeight: '400',
      textTransform: 'uppercase',
      color: (el, s) => (s.active && s.focused ? 'title' : 'caption'),
      text: (el, s) => s.label
    },
    AgentModeColumnCount: {
      tag: 'span',
      color: 'caption',
      hide: (el, s) => !s.count,
      text: (el, s) => String(s.count)
    }
  },
  AgentModeColumnEmpty: {
    padding: 'Z 0',
    color: 'caption',
    hide: (el, s) => s.cards.length > 0,
    text: (el, s) => s.empty || '—'
  },
  AgentModeCards: {
    role: 'listbox',
    aria: { label: (el, s) => s.label },
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    minHeight: '0',
    childExtends: 'AgentModeCard',
    childrenAs: 'state',
    children: (el, s) => s.cards
  }
}

// One card: a title, then its visual and its right-hand text (board.js).
export const AgentModeCard = {
  tag: 'div',
  role: 'option',
  data: { agentId: (el, s) => s.id || null },
  aria: { selected: (el, s) => (s.selected ? 'true' : 'false') },
  display: 'flex',
  flexDirection: 'column',
  gap: 'X',
  padding: 'Y Z',
  borderLeftWidth: 'hairline2',
  borderLeftStyle: 'solid',
  borderLeftColor: (el, s) => (s.selected ? (s.focused ? 'accentInk' : 'caption') : 'transparent'),
  borderBottomWidth: 'hairline',
  borderBottomStyle: 'solid',
  borderBottomColor: 'line',
  background: (el, s) => (s.selected && s.focused ? 'selected' : 'transparent'),
  cursor: 'pointer',
  ':hover': { background: 'hover' },
  stateDeps: [(el, s) => !!s.selected],
  onStateUpdate: (el, s) => el.call('agentModeRevealSelected', el, s),
  onRender: (el, s) => el.call('agentModeRevealSelected', el, s),
  onClick: (e, el, s) => el.call('agentModeClick', s.click, { double: e.detail > 1 }),
  AgentModeCardTitle: {
    color: 'title',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    text: (el, s) => s.title
  },
  AgentModeCardMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: 'Z',
    minWidth: '0',
    hide: (el, s) => !s.visual && !s.right,
    AgentModeVisualSlot: {
      flex: '1',
      minWidth: '0',
      display: 'flex',
      childExtends: 'AgentModeVisual',
      childrenAs: 'state',
      children: (el, s) => (s.visual ? [{ key: 'visual', ...s.visual }] : [])
    },
    AgentModeCardRight: {
      tag: 'span',
      whiteSpace: 'nowrap',
      color: (el, s) => s.rightInk,
      hide: (el, s) => !s.right,
      text: (el, s) => s.right
    }
  }
}

// The small inline visuals of a card (views/widgets.js): a status dot, people
// initials, a progress bar, a spark of bars, or a text.
export const AgentModeVisual = {
  tag: 'span',
  data: { agentVisual: (el, s) => s.kind },
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'X',
  minWidth: '0',
  AgentModeVisualDot: {
    tag: 'span',
    aria: { hidden: 'true' },
    hide: (el, s) => s.kind !== 'dot',
    color: (el, s) => s.ink,
    text: '•'
  },
  AgentModeVisualText: {
    tag: 'span',
    hide: (el, s) => s.kind !== 'text',
    color: (el, s) => s.ink,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    text: (el, s) => s.text
  },
  AgentModeVisualPeople: {
    tag: 'span',
    display: 'inline-flex',
    gap: 'X',
    hide: (el, s) => s.kind !== 'people',
    childExtends: 'AgentModeInitial',
    childrenAs: 'state',
    children: (el, s) => (s.kind === 'people' ? s.people : [])
  },
  AgentModeVisualProgress: {
    tag: 'span',
    role: 'progressbar',
    aria: {
      valuemin: '0',
      valuemax: '100',
      valuenow: (el, s) => String(Math.round((Number(s.value) || 0) * 100))
    },
    hide: (el, s) => s.kind !== 'progress',
    display: 'inline-block',
    width: 'D',
    height: 'X',
    round: 'radiusPill',
    background: 'line',
    overflow: 'hidden',
    AgentModeVisualFill: {
      tag: 'span',
      display: 'block',
      height: '100%',
      background: 'accentInk',
      width: (el, s) => `${Math.round((Number(s.value) || 0) * 100)}%`
    }
  },
  AgentModeVisualBars: {
    tag: 'span',
    aria: { hidden: 'true' },
    hide: (el, s) => s.kind !== 'bars',
    display: 'inline-flex',
    alignItems: 'flex-end',
    gap: 'X',
    height: 'Z',
    childExtends: 'AgentModeBar',
    childrenAs: 'state',
    children: (el, s) => (s.kind === 'bars' ? s.bars.map((b) => ({ ...b, ink: s.ink })) : [])
  }
}

export const AgentModeInitial = {
  tag: 'span',
  title: (el, s) => s.name,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 'A',
  padding: '0 X',
  round: 'radiusControl',
  background: 'selected',
  color: 'title',
  text: (el, s) => s.initial
}

export const AgentModeBar = {
  tag: 'span',
  display: 'inline-block',
  width: 'X',
  background: (el, s) => s.ink,
  height: (el, s) => `${Math.round(s.share * 100)}%`
}

// ── list ───────────────────────────────────────────────────────────────────

// A table of rows, optionally in groups, with the `/` filter (views/list.js).
export const AgentModeList = {
  display: 'flex',
  flexDirection: 'column',
  minWidth: '0',

  AgentModeFilterLine: {
    display: 'flex',
    gap: 'Z',
    marginBottom: 'Z',
    hide: (el, s) => !el.call('agentModeListModel', s.root).filter,
    AgentModeFilterSlash: { tag: 'span', color: 'accentInk', text: '/' },
    AgentModeFilterField: {
      flex: '1',
      display: 'flex',
      childExtends: 'AgentModeField',
      childrenAs: 'state',
      children: (el, s) => {
        const f = el.call('agentModeListModel', s.root).filter
        return f ? [f] : []
      }
    }
  },

  AgentModeListHead: {
    display: 'grid',
    columnGap: 'A',
    paddingBottom: 'Y',
    borderBottomWidth: 'hairline',
    borderBottomStyle: 'solid',
    borderBottomColor: 'line',
    aria: { hidden: 'true' },
    hide: (el, s) => {
      const m = el.call('agentModeListModel', s.root)
      return !m.hasHead || !m.rows.length
    },
    gridTemplateColumns: (el, s) => el.call('agentModeListModel', s.root).template,
    childExtends: 'AgentModeListHeadCell',
    childrenAs: 'state',
    children: (el, s) => {
      const m = el.call('agentModeListModel', s.root)
      return [
        { key: 'hc-marker', label: '', align: 'left' },
        ...(m.hasMark ? [{ key: 'hc-mark', label: '', align: 'left' }] : []),
        ...m.columns
      ]
    }
  },

  AgentModeListRows: {
    role: 'listbox',
    data: { agentList: true },
    aria: {
      label: (el, s) => el.call('agentModeContentOf', el.call('agentModeSnapshot', s.root)).label
    },
    display: 'flex',
    flexDirection: 'column',
    childExtends: 'AgentModeListRow',
    childrenAs: 'state',
    children: (el, s) => el.call('agentModeListModel', s.root).rows
  },

  AgentModeListEmpty: {
    color: 'caption',
    hide: (el, s) => {
      const c = el.call('agentModeContentOf', el.call('agentModeSnapshot', s.root))
      return c.type !== 'list' || el.call('agentModeListModel', s.root).rows.length > 0
    },
    text: (el, s) => {
      const c = el.call('agentModeContentOf', el.call('agentModeSnapshot', s.root))
      const f = el.call('agentModeListModel', s.root).filter
      return f && f.value
        ? el.call('polyglot', 'workspace.agentMode.nothingMatches').replace('{query}', f.value)
        : c.empty || el.call('polyglot', 'workspace.agentMode.nothingHere')
    }
  }
}

export const AgentModeListHeadCell = {
  tag: 'span',
  textTransform: 'uppercase',
  color: 'caption',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textAlign: (el, s) => s.align || 'left',
  text: (el, s) => s.label
}

// One list row, or a group head.
export const AgentModeListRow = {
  tag: 'div',
  role: (el, s) => (s.kind === 'row' ? 'option' : 'presentation'),
  data: { agentId: (el, s) => (s.kind === 'row' && s.id) || null },
  aria: { selected: (el, s) => (s.kind === 'row' ? (s.selected ? 'true' : 'false') : null) },
  display: 'grid',
  columnGap: 'A',
  alignItems: 'center',
  padding: (el, s) => (s.kind === 'group' ? 'Z 0 X' : 'X 0'),
  // A zero-width first track holds the cursor marker (the TUI's ▎).
  gridTemplateColumns: (el, s) => (s.kind === 'group' ? '1fr' : s.template),
  background: (el, s) => (s.selected && s.focused ? 'selected' : 'transparent'),
  cursor: (el, s) => (s.kind === 'row' ? 'pointer' : 'default'),
  ':hover': { background: 'hover' },
  stateDeps: [(el, s) => !!s.selected],
  onStateUpdate: (el, s) => el.call('agentModeRevealSelected', el, s),
  onRender: (el, s) => el.call('agentModeRevealSelected', el, s),
  onClick: (e, el, s) => {
    if (s.kind !== 'row') return
    el.call('agentModeClick', s.click, { double: e.detail > 1 })
  },
  AgentModeRowMarker: {
    tag: 'span',
    aria: { hidden: 'true' },
    hide: (el, s) => s.kind !== 'row',
    width: '0',
    height: '100%',
    borderLeftWidth: 'hairline2',
    borderLeftStyle: 'solid',
    borderLeftColor: (el, s) => (s.selected ? (s.focused ? 'accentInk' : 'caption') : 'transparent')
  },
  AgentModeRowGroup: {
    tag: 'span',
    hide: (el, s) => s.kind !== 'group',
    color: 'caption',
    text: (el, s) => s.text
  },
  AgentModeRowMark: {
    tag: 'span',
    aria: { hidden: 'true' },
    hide: (el, s) => s.kind !== 'row' || !s.mark,
    color: (el, s) => (s.mark && s.mark.ink) || 'title',
    text: (el, s) => (s.mark ? s.mark.text : '')
  },
  AgentModeRowCells: {
    display: 'contents',
    hide: (el, s) => s.kind !== 'row',
    childExtends: 'AgentModeListCell',
    childrenAs: 'state',
    children: (el, s) => (s.kind === 'row' ? s.cells : [])
  }
}

export const AgentModeListCell = {
  tag: 'span',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minWidth: '0',
  textAlign: (el, s) => s.align || 'left',
  color: (el, s) => (s.main ? 'title' : 'caption'),
  fontWeight: (el, s) => (s.main && s.strong ? '700' : '400'),
  text: (el, s) => s.text
}

// ── detail ─────────────────────────────────────────────────────────────────

// One record as a document: title, meta, fields, markdown body, sections
// (views/detail.js).
export const AgentModeDetail = {
  tag: 'article',
  data: { agentId: (el, s) => el.call('agentModeDetailModel', s.root).id || null },
  display: 'flex',
  flexDirection: 'column',
  gap: 'Z',
  maxWidth: 'I',
  AgentModeDetailTitle: {
    tag: 'h2',
    margin: '0',
    fontSize: 'inherit',
    fontWeight: '700',
    color: 'title',
    text: (el, s) => el.call('agentModeDetailModel', s.root).title
  },
  AgentModeDetailMeta: {
    textTransform: 'uppercase',
    color: 'caption',
    hide: (el, s) => !el.call('agentModeDetailModel', s.root).meta,
    text: (el, s) => el.call('agentModeDetailModel', s.root).meta
  },
  AgentModeDetailFields: {
    tag: 'dl',
    margin: '0',
    display: 'grid',
    columnGap: 'B',
    rowGap: 'X',
    padding: 'Z 0',
    borderTopWidth: 'hairline',
    borderTopStyle: 'solid',
    borderTopColor: 'line',
    borderBottomWidth: 'hairline',
    borderBottomStyle: 'solid',
    borderBottomColor: 'line',
    gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 22em), 1fr))',
    hide: (el, s) => !el.call('agentModeDetailModel', s.root).fields.length,
    childExtends: 'AgentModeDetailField',
    childrenAs: 'state',
    children: (el, s) => el.call('agentModeDetailModel', s.root).fields
  },
  AgentModeDetailBody: {
    childExtends: 'AgentModeMarkdown',
    childrenAs: 'state',
    children: (el, s) => {
      const body = el.call('agentModeDetailModel', s.root).body
      return body ? [{ key: `md-${el.call('agentModeTextKey', body)}`, text: body }] : []
    }
  },
  AgentModeDetailSections: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'Z',
    childExtends: 'AgentModeDetailSection',
    childrenAs: 'state',
    children: (el, s) => el.call('agentModeDetailModel', s.root).sections
  }
}

export const AgentModeDetailField = {
  display: 'flex',
  gap: 'Z',
  minWidth: '0',
  AgentModeFieldLabel: {
    tag: 'dt',
    // The TUI's label column: up to 12 cells (detail.js labelW).
    width: 'E',
    flexShrink: '0',
    textTransform: 'uppercase',
    color: 'caption',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    text: (el, s) => s.label
  },
  AgentModeFieldValue: {
    tag: 'dd',
    margin: '0',
    minWidth: '0',
    color: 'paragraph',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    text: (el, s) => s.value
  }
}

export const AgentModeDetailSection = {
  tag: 'section',
  display: 'flex',
  flexDirection: 'column',
  gap: 'Y',
  paddingTop: 'Z',
  borderTopWidth: 'hairline',
  borderTopStyle: 'solid',
  borderTopColor: 'line',
  AgentModeSectionTitle: {
    tag: 'h3',
    margin: '0',
    fontSize: 'inherit',
    fontWeight: '400',
    textTransform: 'uppercase',
    color: 'caption',
    text: (el, s) => s.title
  },
  AgentModeSectionEmpty: {
    color: 'caption',
    hide: (el, s) => s.items.length > 0,
    text: (el, s) => s.empty || el.call('polyglot', 'workspace.agentMode.nothingYet')
  },
  AgentModeSectionItems: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'Z',
    childExtends: 'AgentModeSectionItem',
    childrenAs: 'state',
    children: (el, s) => s.items
  }
}

export const AgentModeSectionItem = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'X',
  AgentModeItemByline: {
    AgentModeItemAuthor: { tag: 'strong', color: 'title', text: (el, s) => s.author },
    AgentModeItemTime: {
      tag: 'span',
      color: 'caption',
      text: (el, s) => (s.time ? ` · ${s.time}` : '')
    }
  },
  AgentModeItemText: {
    paddingLeft: 'Z',
    childExtends: 'AgentModeMarkdown',
    childrenAs: 'state',
    children: (el, s) =>
      s.text ? [{ key: `md-${el.call('agentModeTextKey', s.text)}`, text: s.text }] : []
  }
}

// ── dashboard ──────────────────────────────────────────────────────────────

// Home and Usage: a greeting, number tiles, then board columns
// (views/dashboard.js).
export const AgentModeDashboard = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'B',
  flex: '1',
  minWidth: '0',
  minHeight: '0',
  AgentModeGreetingRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 'A',
    hide: (el, s) => !el.call('agentModeDashboardModel', s.root).greeting,
    AgentModeGreeting: {
      tag: 'h2',
      margin: '0',
      fontSize: 'inherit',
      fontWeight: '700',
      color: 'title',
      text: (el, s) => el.call('agentModeDashboardModel', s.root).greeting
    },
    AgentModeSubtitle: {
      marginLeft: 'auto',
      color: 'caption',
      whiteSpace: 'nowrap',
      text: (el, s) => el.call('agentModeDashboardModel', s.root).subtitle
    }
  },
  AgentModeTiles: {
    display: 'grid',
    gap: 'B',
    gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 14ch), 1fr))',
    hide: (el, s) => !el.call('agentModeDashboardModel', s.root).tiles.length,
    childExtends: 'AgentModeTile',
    childrenAs: 'state',
    children: (el, s) => el.call('agentModeDashboardModel', s.root).tiles
  },
  AgentModePartsFailed: {
    role: 'status',
    color: 'warningInk',
    hide: (el, s) => !el.call('agentModeDashboardModel', s.root).errors,
    text: (el, s) =>
      el
        .call('polyglot', 'workspace.agentMode.partsFailed')
        .replace('{count}', String(el.call('agentModeDashboardModel', s.root).errors))
  },
  AgentModeDashboardBoard: {
    display: 'flex',
    gap: 'B',
    flex: '1',
    minWidth: '0',
    minHeight: '0',
    overflowX: 'auto',
    childExtends: 'AgentModeBoardColumn',
    childrenAs: 'state',
    children: (el, s) => {
      const c = el.call('agentModeContentOf', el.call('agentModeSnapshot', s.root))
      return c.type === 'dashboard' ? el.call('agentModeBoardModel', s.root) : []
    }
  }
}

export const AgentModeTile = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'X',
  minWidth: '0',
  AgentModeTileLabel: {
    textTransform: 'uppercase',
    color: 'caption',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    text: (el, s) => s.label
  },
  AgentModeTileValue: {
    display: 'flex',
    alignItems: 'center',
    gap: 'Y',
    AgentModeTileNumber: { tag: 'strong', color: 'title', text: (el, s) => s.value },
    AgentModeTileDot: {
      tag: 'span',
      aria: { hidden: 'true' },
      hide: (el, s) => !s.dot,
      color: (el, s) => s.dot,
      text: '•'
    }
  },
  AgentModeTileHint: {
    color: 'caption',
    hide: (el, s) => s.progress != null || !s.hint,
    text: (el, s) => s.hint
  },
  AgentModeTileProgress: {
    hide: (el, s) => s.progress == null,
    childExtends: 'AgentModeVisual',
    childrenAs: 'state',
    children: (el, s) =>
      s.progress == null ? [] : [{ key: 'progress', kind: 'progress', value: s.progress }]
  }
}

// ── chat ───────────────────────────────────────────────────────────────────

// Channel rail, messages, composer (views/chat.js).
export const AgentModeChat = {
  display: 'flex',
  flex: '1',
  minWidth: '0',
  minHeight: '0',
  gap: 'A',
  AgentModeChannels: {
    tag: 'nav',
    // The TUI's channel rail: 20 cells (chat.js RAIL).
    width: 'F',
    flexShrink: '0',
    display: 'flex',
    flexDirection: 'column',
    gap: 'X',
    paddingRight: 'Z',
    borderRightWidth: 'hairline',
    borderRightStyle: 'solid',
    borderRightColor: 'line',
    overflowY: 'auto',
    '@tabletSm': { display: 'none' },
    aria: { label: (el) => el.call('polyglot', 'workspace.agentMode.channels') },
    AgentModeChannelsLabel: {
      textTransform: 'uppercase',
      color: 'caption',
      marginBottom: 'Y',
      text: (el) => el.call('polyglot', 'workspace.agentMode.channels')
    },
    AgentModeChannelList: {
      role: 'listbox',
      aria: { label: (el) => el.call('polyglot', 'workspace.agentMode.channels') },
      display: 'flex',
      flexDirection: 'column',
      childExtends: 'AgentModeChannelRow',
      childrenAs: 'state',
      children: (el, s) => el.call('agentModeChatModel', s.root).channels
    }
  },
  AgentModeChatPane: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1',
    minWidth: '0',
    minHeight: '0',
    AgentModeChatEmpty: {
      color: 'caption',
      hide: (el, s) => !!el.call('agentModeChatModel', s.root).active,
      text: (el, s) =>
        el.call(
          'polyglot',
          el.call('agentModeChatModel', s.root).channels.length
            ? 'workspace.agentMode.pickChannel'
            : 'workspace.agentMode.noChannels'
        )
    },
    AgentModeChatHead: {
      display: 'flex',
      gap: 'Z',
      paddingBottom: 'Y',
      borderBottomWidth: 'hairline',
      borderBottomStyle: 'solid',
      borderBottomColor: 'line',
      hide: (el, s) => !el.call('agentModeChatModel', s.root).active,
      AgentModeChatTitle: {
        tag: 'h2',
        margin: '0',
        fontSize: 'inherit',
        fontWeight: '700',
        color: 'title',
        text: (el, s) => (el.call('agentModeChatModel', s.root).active || {}).label || ''
      },
      AgentModeChatTopic: {
        color: 'caption',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        text: (el, s) => el.call('agentModeChatModel', s.root).topic
      }
    },
    AgentModeMessages: {
      role: 'log',
      aria: { label: (el, s) => (el.call('agentModeChatModel', s.root).active || {}).label || '' },
      flex: '1',
      minHeight: '0',
      overflowY: 'auto',
      display: 'flex',
      flexDirection: 'column',
      gap: 'Z',
      padding: 'Z 0',
      hide: (el, s) => !el.call('agentModeChatModel', s.root).active,
      AgentModeNoMessages: {
        color: 'caption',
        hide: (el, s) => el.call('agentModeChatModel', s.root).messages.length > 0,
        text: (el) => el.call('polyglot', 'workspace.agentMode.noMessages')
      },
      AgentModeMessageList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 'Z',
        childExtends: 'AgentModeMessage',
        childrenAs: 'state',
        children: (el, s) => el.call('agentModeChatModel', s.root).messages
      }
    },
    AgentModeComposer: {
      display: 'flex',
      alignItems: 'center',
      gap: 'Z',
      paddingTop: 'Y',
      borderTopWidth: 'hairline',
      borderTopStyle: 'solid',
      borderTopColor: 'line',
      hide: (el, s) => !el.call('agentModeChatModel', s.root).active,
      AgentModePrompt: {
        tag: 'span',
        aria: { hidden: 'true' },
        color: (el, s) => (el.call('agentModeChatModel', s.root).writing ? 'accentInk' : 'caption'),
        text: '›'
      },
      AgentModeComposerIdle: {
        tag: 'button',
        type: 'button',
        flex: '1',
        textAlign: 'left',
        background: 'transparent',
        border: 'none',
        padding: '0',
        font: 'inherit',
        color: 'caption',
        cursor: 'text',
        ':hover': { color: 'title' },
        ':focus-visible': { outline: 'hairline2 solid accentInk' },
        hide: (el, s) => el.call('agentModeChatModel', s.root).writing,
        text: (el, s) =>
          el
            .call('polyglot', 'workspace.agentMode.writeIn')
            .replace('{channel}', (el.call('agentModeChatModel', s.root).active || {}).label || ''),
        onClick: (e, el, s) =>
          el.call('agentModeClick', el.call('agentModeChatModel', s.root).writeClick, {
            double: false
          })
      },
      AgentModeComposerField: {
        flex: '1',
        display: 'flex',
        hide: (el, s) => !el.call('agentModeChatModel', s.root).writing,
        childExtends: 'AgentModeField',
        childrenAs: 'state',
        children: (el, s) => {
          const m = el.call('agentModeChatModel', s.root)
          return [
            {
              ...m.composer,
              placeholder:
                m.composer.placeholder || el.call('polyglot', 'workspace.agentMode.writeMessage')
            }
          ]
        }
      }
    }
  }
}

export const AgentModeChannelRow = {
  tag: 'div',
  role: 'option',
  data: { agentId: (el, s) => s.id },
  aria: { selected: (el, s) => (s.selected ? 'true' : 'false') },
  padding: 'X Z',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  borderLeftWidth: 'hairline2',
  borderLeftStyle: 'solid',
  borderLeftColor: (el, s) => (s.selected ? 'accentInk' : 'transparent'),
  color: (el, s) => (s.selected ? 'title' : 'caption'),
  ':hover': { background: 'hover' },
  text: (el, s) => s.label,
  onClick: (e, el, s) => el.call('agentModeClick', s.click, { double: e.detail > 1 })
}

export const AgentModeMessage = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'X',
  AgentModeMessageByline: {
    AgentModeMessageAuthor: {
      tag: 'strong',
      color: (el, s) => (s.mine ? 'accentInk' : 'title'),
      text: (el, s) => s.author
    },
    AgentModeMessageTime: {
      tag: 'span',
      color: 'caption',
      text: (el, s) => (s.time ? ` · ${s.time}` : '')
    }
  },
  AgentModeMessageText: {
    paddingLeft: 'Z',
    childExtends: 'AgentModeMarkdown',
    childrenAs: 'state',
    children: (el, s) =>
      s.text ? [{ key: `md-${el.call('agentModeTextKey', s.text)}`, text: s.text }] : []
  }
}

// ── Ask AI ─────────────────────────────────────────────────────────────────

// The conversation; the answer streams in (views/assistant.js).
export const AgentModeAssistant = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'A',
  maxWidth: 'I',
  AgentModeAssistantEmpty: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'Y',
    hide: (el, s) => el.call('agentModeAssistantModel', s.root).turns.length > 0,
    AgentModeAssistantTitle: {
      tag: 'h2',
      margin: '0',
      fontSize: 'inherit',
      fontWeight: '700',
      color: 'title',
      text: (el) => el.call('polyglot', 'workspace.agentMode.assistantTitle')
    },
    AgentModeAssistantHint: {
      color: 'caption',
      text: (el) => el.call('polyglot', 'workspace.agentMode.assistantHint')
    },
    AgentModeSuggestions: {
      tag: 'ul',
      margin: 'Z 0 0',
      padding: '0',
      listStyle: 'none',
      display: 'flex',
      flexDirection: 'column',
      gap: 'X',
      childExtends: 'AgentModeSuggestion',
      childrenAs: 'state',
      children: (el, s) => el.call('agentModeAssistantModel', s.root).suggestions
    }
  },
  AgentModeTurns: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'A',
    childExtends: 'AgentModeTurn',
    childrenAs: 'state',
    children: (el, s) => el.call('agentModeAssistantModel', s.root).turns
  }
}

export const AgentModeSuggestion = {
  tag: 'li',
  color: 'caption',
  text: (el, s) => `• ${s.text}`
}

export const AgentModeTurn = {
  data: { agentTurn: (el, s) => s.role },
  display: 'flex',
  flexDirection: 'column',
  gap: 'X',
  AgentModeTurnWho: {
    textTransform: 'uppercase',
    fontWeight: (el, s) => (s.role === 'user' ? '400' : '700'),
    color: (el, s) => (s.role === 'user' ? 'caption' : 'accentInk'),
    text: (el, s) =>
      el.call('polyglot', s.role === 'user' ? 'workspace.agentMode.you' : 'workspace.agentMode.ai')
  },
  AgentModeTurnUser: {
    color: 'title',
    whiteSpace: 'pre-wrap',
    hide: (el, s) => s.role !== 'user',
    text: (el, s) => s.text
  },
  AgentModeTurnAnswer: {
    hide: (el, s) => s.role === 'user',
    childExtends: 'AgentModeMarkdown',
    childrenAs: 'state',
    children: (el, s) =>
      // Parsed ONCE when the answer ends (the chat bubble's rule): a growing
      // half-finished markdown text renders as artefacts.
      s.role !== 'user' && s.text && s.status !== 'streaming'
        ? [{ key: `md-${el.call('agentModeTextKey', s.text)}`, text: s.text }]
        : []
  },
  AgentModeTurnStreamText: {
    color: 'paragraph',
    whiteSpace: 'pre-wrap',
    hide: (el, s) => s.role === 'user' || s.status !== 'streaming',
    text: (el, s) => s.text
  },
  AgentModeTurnStreaming: {
    role: 'status',
    color: 'accentInk',
    hide: (el, s) => s.status !== 'streaming',
    text: '…'
  },
  AgentModeTurnError: {
    role: 'alert',
    color: 'dangerInk',
    hide: (el, s) => s.status !== 'error',
    text: (el, s) => s.error || el.call('polyglot', 'workspace.agentMode.assistantFailed')
  },
  AgentModeTurnHint: {
    color: 'caption',
    hide: (el, s) => s.status !== 'error' || !s.hint,
    text: (el, s) => s.hint
  },
  AgentModeTurnStopped: {
    color: 'caption',
    hide: (el, s) => s.status !== 'stopped',
    text: (el) => el.call('polyglot', 'workspace.agentMode.stopped')
  }
}

// Markdown as DOM: the shared, registered renderer the assistant bubbles use
// (shared functions/renderAssistantMarkdown.js; headings demoted, code kept
// in its block). The row is keyed by its text, so a new text re-mounts it.
// Headings stay the frame's size and weight: one mono line height throughout.
export const AgentModeMarkdown = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'Y',
  minWidth: '0',
  color: 'paragraph',
  // Every block the renderer returns keeps the frame's size: a heading is
  // the line's weight, not a display size (the TUI draws one cell height).
  childProps: { fontSize: 'inherit', lineHeight: 'inherit', margin: '0' },
  children: (el, s) => el.call('renderAssistantMarkdown', s.text || '')
}
